import {IconRescheduleCalendar} from "@bounce/shared/consumer/bounce-ui";
import {useAuth} from "@bounce/shared/contexts";
import {BounceLogo} from "@bounce/shared/ui/assets";
import NiceModal from "@ebay/nice-modal-react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {Box, Divider, Drawer} from "@mui/material";
import {useCallback} from "react";
import {generatePath, useNavigate} from "react-router-dom";
import {useGetAccount, useGetDashboardQuery} from "~/api/requests";
import {
  MenuIconCancelPlan,
  MenuIconClose,
  MenuIconContactUs,
  MenuIconContributionLink,
  MenuIconLogout,
  MenuIconManagePaymentMethods,
  MenuIconMyAccounts,
  MenuIconNoticesAndDisclosures,
  MenuIconOneTimePayment,
  MenuIconPrivacyPolicy,
  MenuIconStateLicenses,
  MenuIconTermsAndConditions,
} from "~/assets";
import {Pages} from "~/bi-events/enum";
import ContactUsDialog from "~/components/contact-us/ContactUsDialog";
import {LanguageSwitcher} from "~/components/LanguageSwitcher";
import {useRewardsProgramState} from "~/components/rewards/hooks/useRewardsProgramState";
import {KEY_OPEN_CANCEL_PLAN_DIALOG} from "~/constants/no-login-keys";
import {useAmplitude, useConsumerUser} from "~/contexts";
import {useAccountId} from "~/hooks";
import {OneTimePaymentDialog} from "~/layout/account/menu/dialogs";
import PreCancelPlanDialog from "~/layout/account/menu/dialogs/PreCancelPlanDialog";
import {useReschedulePlan} from "~/pages/account/account-dashboard/hooks/useReschedulePlan";
import GenerateContributionLinkDialog from "~/pages/account/payment/guest-payment/GenerateContributionLinkDialog";
import {urls} from "~/routes/consumerUrls";
import {Avatar, Container, Text} from "../components";
import {colors, radii, spacing} from "../theme";

type SideMenuProps = {
  open: boolean;
  onClose: () => void;
};

const MenuItem = ({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <Container
    row
    gap={2.5}
    onClick={disabled ? undefined : onClick}
    sx={{
      px: spacing[2],
      py: spacing[2.5],
      borderRadius: radii.xs,
      transition: "background-color 0.15s ease",
      ...(disabled
        ? {opacity: 0.4, cursor: "default"}
        : onClick
        ? {"&:hover": {backgroundColor: colors.primary[100]}}
        : {cursor: "default"}),
    }}
  >
    <Container center sx={{width: 20, height: 20, flexShrink: 0}}>
      {icon}
    </Container>
    <Text variant="body-semibold" color={colors.primary[800]}>
      {label}
    </Text>
  </Container>
);

const MenuDivider = () => <Divider sx={{my: spacing[1], borderColor: colors.tertiary[300]}} />;

const ProfileCard = ({userName, onViewProfile}: {userName: string; onViewProfile: () => void}) => (
  <Container
    row
    gap={2}
    sx={{
      alignItems: "center",
      border: `1px solid ${colors.tertiary[300]}`,
      borderRadius: radii.xs,
      px: spacing[4],
      py: spacing[2],
    }}
    onClick={onViewProfile}
  >
    <Avatar name={userName} size={32} />
    <Box sx={{flex: 1}}>
      <Text variant="body-semibold" color={colors.grey[800]} sx={{lineHeight: "20px"}}>
        {userName}
      </Text>
      <Container row gap={1} sx={{alignItems: "center", cursor: "pointer"}}>
        <Text variant="caption" color={colors.tertiary[600]}>
          View profile
        </Text>
        <KeyboardArrowRightIcon sx={{fontSize: 12, color: colors.tertiary[600]}} />
      </Container>
    </Box>
  </Container>
);

export const SideMenu = ({open, onClose}: SideMenuProps) => {
  const navigate = useNavigate();
  const {clearCredentials, loggedIn, userId} = useAuth();
  const {isNoLogin, userContext} = useConsumerUser();
  const {pageViewEvent, pageClickEvent} = useAmplitude();
  const accountId = useAccountId();
  const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});

  const {rewardsProgramState, shouldDisplayRewardsWarning} = useRewardsProgramState();
  const isAnon = !loggedIn && !isNoLogin;
  const activePaymentPlan = account?.active_payment_plan;
  const activePaymentPlanId = activePaymentPlan?.payment_plan_id;
  const hasActivePlan = !!activePaymentPlan;
  const isAccountResolved = activePaymentPlan?.state === "resolved";
  const userName = userContext?.user_name || "";

  const {data: dashboard} = useGetDashboardQuery(
    {account_id: accountId, payment_plan_id: activePaymentPlanId},
    {enabled: !!activePaymentPlanId}
  );

  // Match the legacy dashboard menu: hide one-time payment for bundle plans,
  // disable it for late accounts that are rewards-eligible.
  const shouldHideOneTimePayment = activePaymentPlan?.payment_plan_type === "BUNDLE";
  const isEligibleForRewards = ["active", "unavailable"].includes(rewardsProgramState?.program_state ?? "");
  const shouldDisableOneTimePayment = activePaymentPlan?.state === "late" && isEligibleForRewards;

  const {handleReschedulePlan, shouldDisplayReschedulePlan} = useReschedulePlan({
    userDashboard: dashboard,
    paymentPlanId: activePaymentPlanId,
    accountId,
    page: Pages.MENU,
    onClose,
  });

  const trackAndClose = useCallback(
    (cta: string) => {
      pageClickEvent({page: Pages.MENU, cta});
      onClose();
    },
    [pageClickEvent, onClose]
  );

  const handleOpen = useCallback(() => {
    pageClickEvent({page: Pages.MENU, cta: "open_menu"});
    pageViewEvent({page: Pages.MENU});
  }, [pageClickEvent, pageViewEvent]);

  const onMyAccounts = () => {
    trackAndClose("home");
    navigate(urls.accounts.url);
  };

  const onViewProfile = () => {
    trackAndClose("view_profile");
    navigate(urls.profile.url);
  };

  const onContactUs = async () => {
    trackAndClose("get_support");
    await NiceModal.show(ContactUsDialog, {fetchContact: loggedIn});
  };

  const onShareLink = async () => {
    trackAndClose("share_contribution_link");
    await NiceModal.show(GenerateContributionLinkDialog, {
      totalBalance: account?.total_balance,
      accountId,
    });
  };

  const onPrivacyPolicy = () => {
    trackAndClose("privacy_policy");
    window.open("https://www.bounceai.com/privacy-policy", "_blank", "noopener,noreferrer");
  };

  const onTermsAndConditions = () => {
    trackAndClose("terms_and_conditions");
    window.open("https://www.bounceai.com/terms-and-conditions", "_blank", "noopener,noreferrer");
  };

  const onStateLicenses = () => {
    trackAndClose("nmls");
    window.open("https://www.bounceai.com/licenses", "_blank", "noopener,noreferrer");
  };

  const onNoticesAndDisclosures = () => {
    trackAndClose("state_notice");
    window.open("https://www.bounceai.com/state-notices", "_blank", "noopener,noreferrer");
  };

  const onOneTimePayment = async () => {
    trackAndClose("one_time_payment");
    if (!accountId) return;
    await NiceModal.show(OneTimePaymentDialog, {
      planBalance: dashboard?.amount_unpaid || 0,
      accountId,
      activePaymentPlanId,
      pendingAmount: dashboard?.pending_amount || 0,
    });
  };

  const onManagePaymentMethods = () => {
    trackAndClose("manage_payment_method");
    if (accountId) {
      navigate(generatePath(urls.managePaymentMethod.url, {accountId}));
    }
  };

  const onCancelPlan = async () => {
    trackAndClose("cancel_plan");
    if (isNoLogin) {
      sessionStorage.setItem(KEY_OPEN_CANCEL_PLAN_DIALOG, "true");
      if (accountId) {
        navigate(generatePath(urls.accountDashboard.url, {accountId}));
      }
    } else {
      await NiceModal.show(PreCancelPlanDialog, {
        accountId,
        shouldDisplayRewardsWarning,
      });
    }
  };

  const onLogout = () => {
    trackAndClose("logout");
    clearCredentials();
    const targetUrl = userId ? `${urls.login.url}?id=${userId}` : urls.login.url;
    navigate(targetUrl);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      onTransitionEnter={handleOpen}
      slotProps={{
        paper: {
          sx: {
            width: 310,
            pt: spacing[2],
            px: spacing[4],
          },
        },
      }}
    >
      <Container row sx={{alignItems: "center", justifyContent: "space-between", height: 64}}>
        <BounceLogo color={colors.primary[800]} width="117px" height="24px" />
        <Box onClick={onClose} sx={{cursor: "pointer", display: "flex", alignItems: "center"}}>
          <MenuIconClose />
        </Box>
      </Container>

      <Box sx={{height: 20}} />

      {!isAnon && userName && <ProfileCard userName={userName} onViewProfile={onViewProfile} />}

      {!isAnon && userName && <Box sx={{height: 20}} />}

      {!isAnon && !hasActivePlan && (
        <>
          <MenuItem icon={<MenuIconMyAccounts />} label="My accounts" onClick={onMyAccounts} />
          <MenuDivider />
          <MenuItem icon={<MenuIconContactUs />} label="Contact us" onClick={onContactUs} />
          <MenuItem icon={<MenuIconContributionLink />} label="Share contribution link" onClick={onShareLink} />
          <MenuItem icon={<MenuIconPrivacyPolicy />} label="Privacy policy" onClick={onPrivacyPolicy} />
          <MenuItem icon={<MenuIconTermsAndConditions />} label="Terms and conditions" onClick={onTermsAndConditions} />
          <MenuItem icon={<MenuIconLogout />} label="Log out" onClick={onLogout} />
        </>
      )}

      {!isAnon && hasActivePlan && (
        <>
          <MenuItem icon={<MenuIconMyAccounts />} label="My accounts" onClick={onMyAccounts} />
          <MenuDivider />
          {!isAccountResolved && (
            <>
              {!shouldHideOneTimePayment && (
                <MenuItem
                  icon={<MenuIconOneTimePayment />}
                  label="One time payment"
                  onClick={onOneTimePayment}
                  disabled={shouldDisableOneTimePayment}
                />
              )}
              <MenuItem
                icon={<MenuIconManagePaymentMethods />}
                label="Payment methods"
                onClick={onManagePaymentMethods}
              />
              {shouldDisplayReschedulePlan && (
                <MenuItem
                  icon={<IconRescheduleCalendar color={colors.primary[800]} />}
                  label="Reschedule plan"
                  onClick={handleReschedulePlan}
                />
              )}
              <MenuItem icon={<MenuIconCancelPlan />} label="Cancel plan" onClick={onCancelPlan} />
            </>
          )}
          <MenuItem icon={<MenuIconContactUs />} label="Contact us" onClick={onContactUs} />
          <MenuItem icon={<MenuIconLogout />} label="Log out" onClick={onLogout} />
        </>
      )}

      {isAnon && (
        <>
          <MenuItem icon={<MenuIconContactUs />} label="Contact us" onClick={onContactUs} />
          <MenuItem icon={<MenuIconPrivacyPolicy />} label="Privacy policy" onClick={onPrivacyPolicy} />
          <MenuItem icon={<MenuIconTermsAndConditions />} label="Terms and conditions" onClick={onTermsAndConditions} />
          <MenuItem icon={<MenuIconStateLicenses />} label="State Licenses" onClick={onStateLicenses} />
          <MenuItem
            icon={<MenuIconNoticesAndDisclosures />}
            label="Notices and disclosures"
            onClick={onNoticesAndDisclosures}
          />
        </>
      )}
      <LanguageSwitcher />
    </Drawer>
  );
};
